// THE HEALTH BOXES GROW WITH THEIR TEXT (TW-0502, 2026-10-26).
//
// The medical history, the medications and blood thinners, and every details box a yes opens take 500 characters since that
// day, where they took 70 (the repo root's form-field-limits.json; docs/FORM-AUDIT-2026-10-26.md, 9: 12 medical histories had
// stopped exactly at 70). A box 150 pixels tall holds five or six lines on a phone, about a third of 500 characters, so the rest
// of what a client wrote would scroll out of sight inside the box while they read it back. Each of the seven boxes starts at the
// 150 it always had and grows with its text, as tall as the words in it, never shorter than 150: an empty box is the box it was.
//
// HOW. Form.tsx hands each box `ref={(box) => growWithText(box)}`. A callback ref written inline is a new function on every
// render, so React calls it after every render with the box, its value already set: the client's own typing and a value the
// page puts there itself (a draft put back after a reload, TW-0500) both reach it. It measures only when the box's value changed
// since it last measured, so a keystroke in another box costs nothing here. A turned phone changes the width the words wrap at,
// so every box is measured again when the window is resized.
//
// The height is the box's own measure, on this device and in this font, the shop app's way (growToLine, apps/app/lib/flashes.ts):
// let go to the floor, then what its words fill (`scrollHeight`, its padding in it) and its border.

/** The height every health box had until TW-0502 (Form.tsx's `style={{ height: 150 }}`), and its floor since. */
export const HEALTH_BOX_FLOOR = 150;

const MARK = "data-grows-with-text";
const measured = new WeakMap<HTMLTextAreaElement, string>();
let listening = false;

/** The box set to the height its words fill, never under the floor; the height it set, in pixels. */
export function fitToText(box: HTMLTextAreaElement): number {
  box.style.height = `${HEALTH_BOX_FLOOR}px`;
  const height = Math.max(HEALTH_BOX_FLOOR, box.scrollHeight + Math.max(0, box.offsetHeight - box.clientHeight));
  box.style.height = `${height}px`;
  return height;
}

/** Every box this has fitted, fitted again at the width the window has now. */
export function refitEveryBox(): void {
  document.querySelectorAll<HTMLTextAreaElement>(`textarea[${MARK}]`).forEach((box) => {
    fitToText(box);
  });
}

/** Form.tsx's ref on each health box: the box fitted to its text when its value changed since the last fit. The null React hands
 *  an old ref is nothing to do. */
export function growWithText(box: HTMLTextAreaElement | null): void {
  if (!box || measured.get(box) === box.value) return;
  measured.set(box, box.value);
  box.setAttribute(MARK, "");
  fitToText(box);
  if (!listening) {
    listening = true;
    window.addEventListener("resize", refitEveryBox);
  }
}
